import { useId, useState } from "react";
import { Button, Checkbox, Select } from "antd";
import {
  ArrowLeftOutlined,
  LeftOutlined,
  DownOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import PlotSummary from "../PlotSummary";
import SeedRateInput, { SEED_RATE_CONTROLS } from "../SeedRateInput";
import {
  type SeedProduct,
  type SeedStripe,
  type StripeAssignment,
  type StripeAssignments,
} from "../../utils/seedFlow";
import useSeedTranslations from "../../hooks/useSeedTranslations";
import "./style.less";
import { usePlotOutline } from "../../hooks/usePlotOutline";
import { getSeedProductColor } from "../../utils/seedProductColors";

interface StripeAutoAssignmentProps {
  products?: SeedProduct[];
  standardProductId?: string;
  unit?: string;
  stripes: SeedStripe[];
  assignments: StripeAssignments;
  standardStripeIds: string[];
  canUseStandardRate: boolean;
  errors: Record<string, string | undefined>;
  validAssignmentCount: number;
  isComplete: boolean;
  onAssignmentChange: (
    stripeId: string,
    update: Partial<StripeAssignment>
  ) => void;
  onStandardRateChange: (stripeId: string, checked: boolean) => void;
  onBack: () => void;
  onContinue: () => void;
  nextLabel?: string;
}

const StripeAutoAssignment = ({
  products = [],
  standardProductId,
  unit = "seeds/ha",
  stripes,
  assignments,
  standardStripeIds,
  canUseStandardRate,
  errors,
  validAssignmentCount,
  isComplete,
  onAssignmentChange,
  onStandardRateChange,
  onBack,
  onContinue,
  nextLabel,
}: StripeAutoAssignmentProps): JSX.Element => {
  const text = useSeedTranslations();
  const instanceId = useId();
  const { isOutlined, toggleOutline } = usePlotOutline();
  const tableId = `${instanceId}-stripe-auto-table`;

  const [collapsed, setCollapsed] = useState(false);
  const [highlightedStripeId, setHighlightedStripeId] = useState<string | null>(
    null
  );

  const productOptions = products.map((product) => ({
    value: product.id,
    label:
      product.id === standardProductId
        ? text("standardProductOption", { product: product.name })
        : product.name,
  }));
  const seedColorProducts = products
    .filter((product) => product.id !== standardProductId)
    .map((product) => ({
      id: product.id,
      name: product.name,
      options: product.densityOptions,
      range: { min: product.min, max: product.max },
    }));

  const usedProductCount = new Set(
    stripes.map((stripe) => assignments[stripe.id]?.productId).filter(Boolean)
  ).size;

  return (
    <div className="rd-stripe-auto" data-testid="stripe-auto-assignment">
      <button
        type="button"
        className="rd-stripe-auto__heading"
        onClick={onBack}
      >
        <ArrowLeftOutlined aria-hidden />
        <span>{text("autoProductAssignment")}</span>
      </button>

      <PlotSummary
        totalPlots={stripes.length}
        assigned={validAssignmentCount}
        totalLabel={text("totalStripes")}
        assignedLabel={text("selected")}
        missingLabel={text("missing")}
        applicationAssigned={usedProductCount}
        applicationTotal={products.length}
        applicationLabel={text("products")}
      />

      <section className="rd-stripe-auto__section">
        <button
          type="button"
          className="rd-stripe-auto__section-toggle"
          aria-expanded={!collapsed}
          aria-controls={tableId}
          onClick={() => {
            setCollapsed((previous) => !previous);
          }}
        >
          {collapsed ? (
            <RightOutlined aria-hidden />
          ) : (
            <DownOutlined aria-hidden />
          )}
          <span>{text("strips")}</span>
        </button>

        <div
          id={tableId}
          className="rd-stripe-auto__table-wrapper"
          hidden={collapsed}
        >
          <table className="rd-stripe-auto__table">
            <thead>
              <tr>
                <th scope="col">{text("strips")}</th>
                <th scope="col">{text("standardRateQuestion")}</th>
                <th scope="col">{text("product")}</th>
                <th scope="col">
                  {text("seedRate")} <span>({unit})</span>
                </th>
                <th scope="col">
                  <span className="rd-stripe-auto__sr-only">
                    {text("highlightStripe")}
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              {stripes.map((stripe, index) => {
                const assignment = assignments[stripe.id];

                const product = products.find(
                  (item) => item.id === assignment?.productId
                );
                const seedColorProduct = seedColorProducts.find(
                  (item) => item.id === product?.id
                );
                const rowColor =
                  product === undefined
                    ? "#dce0e7"
                    : product.id === standardProductId ||
                      seedColorProduct === undefined
                    ? product.color
                    : getSeedProductColor(
                        seedColorProduct,
                        seedColorProducts,
                        assignment?.rate,
                        [assignment?.rate]
                      );

                const usesStandardRate = standardStripeIds.includes(stripe.id);
                const highlighted = highlightedStripeId === stripe.id;
                const error = errors[stripe.id];
                const rateError = product === undefined ? undefined : error;
                const errorId = `${instanceId}-${stripe.id}-error`;

                return (
                  <tr
                    key={stripe.id}
                    className={
                      highlighted
                        ? "rd-stripe-auto__row--highlighted"
                        : undefined
                    }
                  >
                    <th
                      scope="row"
                      style={{
                        borderLeftColor: rowColor,
                      }}
                    >
                      {index + 1}
                    </th>

                    <td>
                      <Checkbox
                        aria-label={`${text("standardRateQuestion")} ${
                          stripe.label
                        }`}
                        checked={usesStandardRate}
                        disabled={
                          !assignment?.productId ||
                          (!canUseStandardRate && !usesStandardRate)
                        }
                        onChange={(event) => {
                          onStandardRateChange(stripe.id, event.target.checked);
                        }}
                      />
                    </td>

                    <td>
                      <Select<string>
                        aria-label={text("stripeProductAria", {
                          stripe: stripe.label,
                        })}
                        value={
                          assignment?.productId === ""
                            ? undefined
                            : assignment?.productId
                        }
                        options={productOptions}
                        disabled={usesStandardRate}
                        placeholder={text("select")}
                        status={!product && error ? "error" : undefined}
                        aria-invalid={!product && Boolean(error)}
                        onChange={(productId) => {
                          onAssignmentChange(stripe.id, { productId });
                        }}
                      />
                    </td>

                    <td>
                      <SeedRateInput
                        controls={SEED_RATE_CONTROLS}
                        options={product?.densityOptions}
                        disabled={usesStandardRate}
                        aria-label={text("stripeRateAria", {
                          stripe: stripe.label,
                        })}
                        value={assignment?.rate ?? null}
                        status={rateError ? "error" : undefined}
                        aria-invalid={Boolean(rateError)}
                        aria-describedby={rateError ? errorId : undefined}
                        onChange={(rate) => {
                          onAssignmentChange(stripe.id, { rate });
                        }}
                      />

                      {rateError && (
                        <span
                          id={errorId}
                          className="rd-stripe-auto__error"
                          role="alert"
                        >
                          {rateError}
                        </span>
                      )}
                    </td>

                    <td>
                      <Button
                        type="text"
                        className="rd-stripe-auto__eye"
                        icon={
                          isOutlined(stripe.id) ? (
                            <EyeOutlined />
                          ) : (
                            <EyeInvisibleOutlined />
                          )
                        }
                        aria-label={text("highlightStripeAria", {
                          stripe: stripe.label,
                        })}
                        aria-pressed={isOutlined(stripe.id)}
                        onClick={() => {
                          toggleOutline(stripe.id);
                          setHighlightedStripeId((previous) =>
                            previous === stripe.id ? null : stripe.id
                          );
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {stripes.length === 0 && <p role="status">{text("noStripes")}</p>}
        </div>
      </section>

      <div className="rd-stripe-auto__footer">
        <Button type="text" onClick={onBack}>
          <LeftOutlined aria-hidden />
          {text("back")}
        </Button>

        <Button type="primary" disabled={!isComplete} onClick={onContinue}>
          {nextLabel ?? text("biologicals")}
          <RightOutlined aria-hidden />
        </Button>
      </div>
    </div>
  );
};

export default StripeAutoAssignment;
